import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box, 
  Button, 
  Container, 
  Heading, 
  Text, 
  Flex, 
  SimpleGrid, 
  VStack,
  Icon,
  Divider,
  useColorModeValue,
  useMediaQuery
} from "@chakra-ui/react";
import axios from "axios";
import { Link } from "react-router-dom";
import Loading from "./resources/Loading";
import { FaLeaf, FaTractor, FaTruckMoving, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import './Home.css'; // Import the custom CSS
import './components/Weather/Weather.css';
import './MobileHeader.css';
import sell_icon from './Assets/sell_icon.png';
import store_icon from './Assets/store_icon.png';
import businessmen_icon from './Assets/businessmen_icon.png';
import animaltrade_icon from './Assets/animaltrade_icon.png';
import animalinsurance_icon from './Assets/animalinsurance_icon.png';
import mandi from './Assets/mandi.avif';
import government_icon from './Assets/science_icon.png';
import fasalbima_icon from './Assets/fasalbima_icon.png';

import fruits_banner from './Assets/fruit_banner.jpeg';
import vegirable_banner from './Assets/vegitable.jpeg';
import dairy_banner from './Assets/dairy.jpeg';
import equipement_banner from './Assets/equipement.jpeg';

import { useTranslation } from "./translations/useTranslation";

// Import weather components
import EnhancedWeatherForecast from "./components/Weather/EnhancedWeatherForecast";
import MobileWeatherWidget from "./components/Weather/MobileWeatherWidget";

// Compact product card component for horizontal scrolling
function CompactProductCard({ product }) {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  
  // Format the price with the unit
  const formatPriceWithUnit = () => {
    if (!product.unit) return `₹${product.price}`;
    return `₹${product.price}/${product.unit}`;
  };

  return (
    <div className="compact-product-card">
      <img 
        src={product.images && product.images.length > 0 ? product.images[0] : '/placeholder-image.jpg'} 
        alt={product.title} 
        className="compact-product-image"
        loading="lazy"
      />
      <div className="compact-product-content">
        <h3 className="compact-product-title">{product.title}</h3>
        <div className="compact-product-category">{product.catagory}</div>
        {!isMobile && (
          <div className="compact-product-meta">
            {product.quantity && product.unit ? `${product.quantity} ${product.unit}` : ''}
          </div>
        )}
        {isMobile && product.quantity && product.unit && (
          <div className="compact-product-meta">
            {`${product.quantity} ${product.unit}`}
          </div>
        )}
        <div className="compact-product-price">{formatPriceWithUnit()}</div>
      </div>
    </div>
  );
}

// Category icon component for mobile view
function CategoryIcon({ icon, label, to, auth, setStaticModal, toggleShow, setActiveTab }) {
  const handleClick = () => {
    if (to === "/sell" && !auth) {
      // Show login message for unauthenticated users trying to access sell
      setStaticModal(true);
      setActiveTab("tab1");
      setTimeout(() => {
        toggleShow();
      }, 100);
      return;
    }
    // For other links or authenticated users, navigate normally
    window.location.href = to || "/";
  };

  return (
    <div onClick={handleClick} style={{ cursor: 'pointer' }}>
      <VStack spacing={1} className="category-icon">
        <Box className="category-icon-circle">
          <img src={icon} alt={label} className="category-icon-image" loading="lazy" />
        </Box>
        <Text fontSize="xs" fontWeight="medium" textAlign="center">{label}</Text>
      </VStack>
    </div>
  );
}

function ProductRow({ title, products, category }) {
  const scrollRef = useRef(null);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation();
  
  const scroll = (direction) => {
    const { current } = scrollRef;
    if (current) {
      const scrollAmount = isMobile ? (direction === 'left' ? -350 : 350) : (direction === 'left' ? -800 : 800);
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Limit products to improve performance
  const limitedProducts = useMemo(() => products.slice(0, 20), [products]);

  return (
    <div className="product-row-container">
      <div className="product-row-header">
        <h2 className="category-title">{t(title.toLowerCase())}</h2>
        <Link to={`/${category}`} className="see-all-link">
          {t('seeAll')}
        </Link>
      </div>
      
      <div className="product-row-scroll" ref={scrollRef}>
        {limitedProducts.map((product) => (
          <div key={product._id} className="product-card-wrapper">
            <Link to={`/preview_ad/${product._id}`} style={{ display: 'block', height: '100%' }}>
              <CompactProductCard product={product} />
            </Link>
          </div>
        ))}
      </div>
      
      <button 
        className="scroll-button scroll-button-left"
        onClick={() => scroll('left')}
        aria-label="Scroll left"
      >
        <FaChevronLeft className="scroll-button-icon" />
      </button>
      
      <button 
        className="scroll-button scroll-button-right"
        onClick={() => scroll('right')}
        aria-label="Scroll right"
      >
        <FaChevronRight className="scroll-button-icon" />
      </button>
    </div>
  );
}

function Home({ auth, setStaticModal, toggleShow, setActiveTab }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleproducts, setVisibleProducts] = useState(12); // Reduced initial load
  const hasMoreProductsToLoad = visibleproducts < products.length;
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { t } = useTranslation();

  useEffect(() => {
    // Add or remove class to body based on mobile screen
    if (isMobile) {
      document.body.classList.add('show-weather-widget');
    } else {
      document.body.classList.remove('show-weather-widget');
    }

    return () => {
      document.body.classList.remove('show-weather-widget');
    };
  }, [isMobile]);

  // Memoized API call to prevent unnecessary re-renders
  const getProducts = useCallback(async () => {
    try {
      // Use a more efficient endpoint or implement pagination
      const response = await axios.get("https://api.agromanch.com/getProducts?limit=50");
      // Products are already sorted by createdAt on the server
      setProducts(response.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  // Group products by category with useMemo for performance
  // Move all useMemo hooks to the top level to fix ESLint errors
  const grainProducts = useMemo(() => products.filter(product => product.catagory === "Grains").slice(0, 10), [products]);
  const fruitProducts = useMemo(() => products.filter(product => product.catagory === "Fruits").slice(0, 10), [products]);
  const vegetableProducts = useMemo(() => products.filter(product => product.catagory === "Vegetables").slice(0, 10), [products]);
  const livestockProducts = useMemo(() => products.filter(product => product.catagory === "Livestock").slice(0, 10), [products]);
  const dairyProducts = useMemo(() => products.filter(product => product.catagory === "Dairy").slice(0, 10), [products]);
  const spicesProducts = useMemo(() => products.filter(product => product.catagory === "Spices").slice(0, 10), [products]);
  
  // Get featured products (newest or with quality grade A)
  const featuredProducts = useMemo(() => products
    .filter(product => product.quality === "A")
    .slice(0, 8), [products]);
    
  // Get recently added products
  const recentlyAddedProducts = useMemo(() => products.slice(0, 8), [products]);

  // Category icons for mobile view
  const categoryIcons = [
    { icon: sell_icon, label: t('sell'), to: "/sell" },
    { icon: store_icon, label: t('store'), to: "/stores" },
    { icon: businessmen_icon, label: t('businessmen'), to: "/businessmen" },
    { icon: animaltrade_icon, label: t('animalTrade'), to: "/livestock"},
    { icon: animalinsurance_icon, label: t('animalInsurance'), to: "pashu-bima" },
    { icon: mandi, label: t('mandiRate'), to: "/mandi-bhav" },
    { icon: fasalbima_icon, label: t('FasalBima'), to: "/fasal-bima" },
    { icon: government_icon, label: t('adminStore'), to: "/admin-store" },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <Box>
      
      {/* Weather section - show different component based on screen size */}
      <Container maxW="container.xl" my={isMobile ? 0 : 6}>
        {isMobile ? (
          <>
            <MobileWeatherWidget />
            
            {/* Commodities and Food Section - Mobile Only */}
            <Box mb={4}>
              <Text fontSize="md" fontWeight="bold" mb={3}>{t('commoditiesAndFood')}</Text>
              <SimpleGrid columns={4} spacing={3} mb={3}>
                {categoryIcons.slice(0, 4).map((item, index) => (
                  <CategoryIcon key={index} icon={item.icon} label={item.label} to={item.to} auth={auth} setStaticModal={setStaticModal} toggleShow={toggleShow} setActiveTab={setActiveTab} />
                ))}
              </SimpleGrid>
              <SimpleGrid columns={4} spacing={3}>
                {categoryIcons.slice(4, 8).map((item, index) => (
                  <CategoryIcon key={index + 4} icon={item.icon} label={item.label} to={item.to} auth={auth} setStaticModal={setStaticModal} toggleShow={toggleShow} setActiveTab={setActiveTab} />
                ))}
              </SimpleGrid>
            </Box>
          </>
        ) : (
          <>
            <EnhancedWeatherForecast />
            
            {/* Commodities and Food Section - Desktop/Laptop */}
            <Box mb={6} mt={6}>
              <Text fontSize="lg" fontWeight="bold" mb={4}>{t('commoditiesAndFood')}</Text>
              <SimpleGrid columns={5} spacing={4} mb={4}>
                {categoryIcons.slice(0, 5).map((item, index) => (
                  <CategoryIcon key={index} icon={item.icon} label={item.label} to={item.to} auth={auth} setStaticModal={setStaticModal} toggleShow={toggleShow} setActiveTab={setActiveTab} />
                ))}
              </SimpleGrid>
              <SimpleGrid columns={4} spacing={4}>
                {categoryIcons.slice(5, 9).map((item, index) => (
                  <CategoryIcon key={index + 5} icon={item.icon} label={item.label} to={item.to} auth={auth} setStaticModal={setStaticModal} toggleShow={toggleShow} setActiveTab={setActiveTab} />
                ))}
              </SimpleGrid>
            </Box>
          </>
        )}
      </Container>

      {/* Category Feature Boxes */}
      <Container maxW="container.xl" my={6}>
        {/* Recently Added Products Section */}
        {recentlyAddedProducts.length > 0 && (
          <ProductRow 
            title={t('recentlyAddedProducts')} 
            products={recentlyAddedProducts} 
            category="Products" 
          />
        )}
      
        {/* Featured Banners */}
        <Box mb={10}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Banner 1 */}
            <Link to="/Fruits">
              <Box 
                p={0} 
                className="feature-banner banner-green fade-in"
              >
                <div className="banner-content">
                  <h2 className="banner-heading">{t('freshSeasonalFruits')}</h2>
                  <p className="banner-text">{t('directlyFromOrchards')}</p>
                  <span className="banner-badge">{t('upTo')} 20% {t('off')}</span>
                </div>
                <img 
                  src={fruits_banner}
                  alt="Seasonal Fruits" 
                  className="banner-image"
                  loading="lazy"
                />
              </Box>
            </Link>

            {/* Banner 2 */}
            <Link to="/Vegetables">
              <Box 
                p={0}
                className="feature-banner banner-orange fade-in-delay-1"
              >
                <div className="banner-content">
                  <h2 className="banner-heading">{t('organicVegetables')}</h2>
                  <p className="banner-text">{t('chemicalFreeNaturallyGrown')}</p>
                  <span className="banner-badge">{t('freshlyHarvested')}</span>
                </div>
                <img 
                  src={vegirable_banner}
                  alt={t('organicVegetables')} 
                  className="banner-image"
                  loading="lazy"
                />
              </Box>
            </Link>
          </SimpleGrid>
        </Box>

        {/* Featured Products Section */}
        {featuredProducts.length > 0 && (
          <ProductRow 
            title={t('premiumQuality')} 
            products={featuredProducts} 
            category="Featured" 
          />
        )}

        {/* Full Width Banner */}
        <Link to="/Dairy">
          <Box 
            p={0}
            className="feature-banner banner-blue full-width-banner fade-in"
          >
            <div className="banner-content">
              <h2 className="banner-heading">{t('farmFreshDairyProducts')}</h2>
              <p className="banner-text">{t('pureMilkDescription')}</p>
              <span className="banner-badge">{t('premiumQuality')}</span>
            </div>
            <img 
              src={dairy_banner}
              alt={t('dairyProducts')} 
              className="banner-image"
              loading="lazy"
            />
          </Box>
        </Link>

        {/* Categories Sections with improved layout */}
        {grainProducts.length > 0 && (
          <ProductRow 
            title="Grains" 
            products={grainProducts} 
            category="Grains" 
          />
        )}
        
        {vegetableProducts.length > 0 && (
          <ProductRow 
            title="Vegetables" 
            products={vegetableProducts} 
            category="Vegetables" 
          />
        )}
        
        {fruitProducts.length > 0 && (
          <ProductRow 
            title="Fruits" 
            products={fruitProducts} 
            category="Fruits" 
          />
        )}
        
        {dairyProducts.length > 0 && (
          <ProductRow 
            title="Dairy" 
            products={dairyProducts} 
            category="Dairy" 
          />
        )}

        {livestockProducts.length > 0 && (
          <ProductRow 
            title="Livestock" 
            products={livestockProducts} 
            category="Livestock" 
          />
        )}
        
        {spicesProducts.length > 0 && (
          <ProductRow 
            title="Spices" 
            products={spicesProducts} 
            category="Spices" 
          />
        )}

        {/* Farm Equipment Banner */}
        <Link to="/Farm Equipment">
          <Box 
            p={0}
            className="feature-banner banner-yellow full-width-banner fade-in"
          >
            <div className="banner-content">
              <h2 className="banner-heading">{t('farmingEquipment')}</h2>
              <p className="banner-text">{t('farmingEquipmentDescription')}</p>
              <span className="banner-badge">{t('bestDeals')}</span>
            </div>
            <img 
              src={equipement_banner}
              alt={t('farmingEquipment')} 
              className="banner-image"
              loading="lazy"
            />
          </Box>
        </Link>

        {/* All Products */}
        <Box mb={10} mt={10}>
          <Heading as="h2" size="lg" mb={6} color="green.700" className="section-heading">
            {t('allProducts')}
          </Heading>
          <Divider mb={6} />
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
            {products.slice(0, visibleproducts).map((product) => (
              <Link key={product._id} to={`/preview_ad/${product._id}`} style={{ display: 'block', height: '100%' }}>
                <Box className="product-card-compact">
                  <CompactProductCard product={product} />
                </Box>
              </Link>
            ))}
          </SimpleGrid>
          {hasMoreProductsToLoad && (
          <Flex justify="center" mt={8}>
            <Button
              className="mb-2 mt-6"
              bgGradient="linear(to-r, green.400, teal.500)"
              color="white"
              size="lg"
              _hover={{
                bgGradient: "linear(to-r, green.500, teal.600)",
              }}
              _active={{
                bgGradient: "linear(to-r, green.600, teal.700)",
              }}
              onClick={() => {
                setVisibleProducts((prev) => prev + 12); // Load in smaller chunks
              }}
            >
              {t('loadMore')}
            </Button>
          </Flex>
        )}
        </Box>

        {/* Add a Call to Action Section */}
        <Box className="cta-section fade-in" mb={10}>
          <Container maxW="container.md" textAlign="center" py={10}>
            <Heading as="h2" size="xl" mb={4} className="cta-heading">
              {t('joinAgriculturalRevolution')}
            </Heading>
            <Text fontSize="lg" mb={6} className="cta-text">
              {t('connectWithFarmers')}
            </Text>
            {auth ? (
              <Link to="/sell">
                <Button size="lg" className="cta-button">
                  {t('startSellingNow')}
                </Button>
              </Link>
            ) : (
              <Button size="lg" className="cta-button" onClick={() => {
                setStaticModal(true);
                setActiveTab("tab1");
                setTimeout(() => {
                  toggleShow();
                }, 100);
              }}>
                {t('startSellingNow')}
              </Button>
            )}
          </Container>
        </Box>
      </Container>
        
      {/* Benefits Banner */}
      <Box py={6} className="benefits-section">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={10}>
            <VStack align="center" className="benefit-box fade-in">
              <Icon as={FaLeaf} w={10} h={10} color="green.500" className="benefit-icon" />
              <Text fontWeight="bold" fontSize="lg" className="benefit-title">{t('freshProducts')}</Text>
              <Text textAlign="center" color="gray.600" className="benefit-text">{t('directFromFarms')}</Text>
            </VStack>
            <VStack align="center" className="benefit-box fade-in-delay-1">
              <Icon as={MdVerified} w={10} h={10} color="green.500" className="benefit-icon" />
              <Text fontWeight="bold" fontSize="lg" className="benefit-title">{t('qualityAssured')}</Text>
              <Text textAlign="center" color="gray.600" className="benefit-text">{t('qualityCheckedProducts')}</Text>
            </VStack>
            <VStack align="center" className="benefit-box fade-in-delay-1">
              <Icon as={FaTractor} w={10} h={10} color="green.500" className="benefit-icon" />
              <Text fontWeight="bold" fontSize="lg" className="benefit-title">{t('supportFarmers')}</Text>
              <Text textAlign="center" color="gray.600" className="benefit-text">{t('buyDirectlyFromFarmers')}</Text>
            </VStack>
            <VStack align="center" className="benefit-box fade-in-delay-2">
              <Icon as={FaTruckMoving} w={10} h={10} color="green.500" className="benefit-icon" />
              <Text fontWeight="bold" fontSize="lg" className="benefit-title">{t('fastDelivery')}</Text>
              <Text textAlign="center" color="gray.600" className="benefit-text">{t('quickReliableShipping')}</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;